export interface MarketData {
  symbol: string;
  value: number;
  change: number;
  changePercent: number;
}

// Updated to match PositionTable structure
export interface Leg {
  id: string;
  direction: 'long' | 'short'; // Changed from 'B' | 'S' to match PositionTable
  expiry: string;
  strike: string;
  lots: number;
  ltp: number;
  entryTime?: string;
  contractType?: string;
  entryPrice?: number;
  isActive?: boolean;
  iv?: number; // Added to match PositionBaseDataType
  premium?: number; // Added to match PositionBaseDataType
  contractDetails?: SymbolOptionChainData; // Added to match PositionBaseDataType
}

export interface Strategy {
  name: string;
  description: string;
  payoffType: 'straddle' | 'strangle' | 'bullCall' | 'bearPut' | 'ironFly' | 'ironCondor';
}

export interface OptionTabMetaData {
  symbol: string;
  optionExpiry: string;
  future: string;
}

// API Types
export interface SymbolMetadata {
  lot_sizes: Record<string, number>;
  futures: Record<string, Array<string>>;
  options: Record<string, Array<string>>;
}

export interface GetOptionChainResultDataType {
  index: string;
  expiry: string;
  timestamp: string;
  spot_price: number;
  candles: Record<string, unknown>;
  futures: Record<string, unknown>;
}

// Option Chain Data Types
export interface SymbolOptionChainData {
  close?: number;
  delta?: number;
  gamma?: number;
  iv?: number;
  rho?: number;
  theta?: number;
  vega?: number;
  contractType?: string;
}

export interface StrikeData {
  CE?: SymbolOptionChainData;
  PE?: SymbolOptionChainData;
}

export interface OptionChainTableRow {
  strike: string;
  ceLTP: number;
  peLTP: number;
  isATM: boolean;
  isITM: boolean;
  isOTM: boolean;
  cePosition?: PositionData;
  pePosition?: PositionData;
}

// Updated to match BuySellButton requirements
export interface PositionData {
  direction: 'long' | 'short';
  lots: number;
  premium: number;
  contractType: string;
  strike: string;
  iv?: number;
  entryTime?: string;
  contractDetails?: SymbolOptionChainData;
  expiry?: string; // Added for type safety
}

// Add Simulator-like structure for expiry-wise positions
export interface PositionWithExpiryData {
  [expiryDate: string]: Record<string, PositionData>;
}

// Safe type guards
export const isString = (value: unknown): value is string => {
  return typeof value === 'string';
};

export const isNumber = (value: unknown): value is number => {
  return typeof value === 'number';
};

export const isArray = (value: unknown): value is Array<unknown> => {
  return Array.isArray(value);
};

export const isStrikeData = (value: unknown): value is StrikeData => {
  return typeof value === 'object' && value !== null && ('CE' in value || 'PE' in value);
};

// Utility function for MTM calculation (matching PositionTable logic)
export const calculateMTM = (entryPrice: number, currentLTP: number, lots: number, direction: 'long' | 'short', lotSize = 1): number => {
  if (direction === 'long') {
    return (currentLTP - entryPrice) * lotSize * lots;
  } else {
    return -(currentLTP - entryPrice) * lotSize * lots;
  }
};

export interface ChartSidebarProps {
  onLegAdd?: (leg?: Leg) => void;
  onLegEdit?: (leg: Leg) => void;
  onLegDelete?: (legId: string) => void;
  onClearLegs?: () => void;
  marketData?: MarketData[];
  legs?: Leg[];
  showUnderlying?: boolean;
  onShowUnderlyingChange?: (show: boolean) => void;
  // New props for symbol selection
  optionTabMetaData?: OptionTabMetaData;
  setOptionTabMetaData?: (option: OptionTabMetaData) => void;
  optionChainData?: GetOptionChainResultDataType;
  optionChainMetadata?: SymbolMetadata;
  indexes?: string[];
  onSymbolChange?: (symbol: string) => void;
  lotSize?: number; // Added to match PositionTable
  // New props for position persistence
  positions?: PositionWithExpiryData; // Updated to use expiry-wise structure
  onPositionUpdate?: (positions: PositionWithExpiryData) => void; // Updated to use expiry-wise structure
  onPositionFlagTrigger?: () => void;

} 